/**
 * Rectangle element renderer
 * Extracted from Canvas.jsx to reduce file size
 */
import React from "react";
import { flushSync } from "react-dom";
import RectangleElement from "../../rectangle/RectangleElement";
import { getOffCanvasStageWidthPx } from "../../../utils/mainCanvasLayout";
import { isElementInViewport } from "../../../utils/viewportCullingUtils";
import { isPrimaryMouseButton } from "../../../utils/pointerButtonUtils";
import { constrainOffCanvasLiveTransformAttrs } from "../../../utils/offCanvasTransformUtils";

// Reduced from 80ms to 16ms for better cross-canvas drag performance
const DRAG_STATE_THROTTLE_MS = 16;
let _lastRectDragStateTime = 0;

/**
 * Creates a rectangle element renderer factory function
 */
export const createRectangleRenderer = ({
  rectangles,
  offCanvasRectangles,
  setRectangles,
  setOffCanvasRectangles,
  selectedObject,
  selectedObjects = [], // Add selectedObjects parameter
  strokeColor,
  images,
  circles = [], // Add for cross-canvas check
  texts = [], // Add for cross-canvas check
  arrows = [], // Add for cross-canvas check
  lines = [], // Add for cross-canvas check
  doubleSidedArrows = [], // Add for cross-canvas check
  canvasHeight,
  canvasResizeTrigger,
  effectiveZoomLevel,
  disableInteraction,
  debouncedStateUpdate,
  isTransformingRef,
  setSelectedObject,
  setSelectedObjects,
  setShowTextBox,
  setSelectedText,
  setSelectedImage,
  setSelectedArrow, // Add setSelectedArrow to clear arrow selections
  setSelectedDoubleSidedArrow, // Add setSelectedDoubleSidedArrow to clear double-sided arrow selections
  setSelectedLine, // Add setSelectedLine to clear line selections
  onClick,
  handleElementDragEnd,
  saveToHistory,
  handleDragPreview,
  clearDragPreview,
  handleRectangleCopy,
  lockedAction,
  isCloneMode = false, // Clone mode flag (additional check for clone mode)
  isElementBeingEdited = false,
  setPendingChanges = () => {},
  pendingChangesRef = null, // Ref to update synchronously
  isMultiSelectMode = false, // Multi-select mode flag
  handleGroupDragStart = null, // Handler for group drag start
  handleGroupDragMove = null, // Handler for group drag move
  cancelActiveGroupDrag = null, // Clear group-drag refs when multi-select toggles off mid-gesture
  stageRef = null, // Main canvas stage ref
  offCanvasStageRef = null, // Off-canvas stage ref
  justFinishedTransformRef = null, // Ref to skip clearing selection on next stage click after transform end
  lastTransformStartTimeRef = null, // Time-based fallback for transform-end click skip
  selectionBoxRef = null, // Clear on transform start so global mouseup doesn't treat release as selection-box
  selectionBoxStageRef = null,
  blockClicksOnOffCanvasElements = false, // When true, off-canvas elements ignore clicks (multi-select + main stage selection)
  blockClicksOnMainStageElements = false, // When true, main stage elements ignore clicks (multi-select + off-canvas selection)
  clearDebouncedTransformForKey = null,
  crossStageGestureBegin = null,
  crossStageGestureEnd = null,
}) => {
  return ({
    rectangle,
    isOffCanvas = false,
    sourceStageRef,
    isPreview = false,
    opacity = undefined, // Opacity override for dimming non-selected elements
  }) => {
    const setRectState = isOffCanvas ? setOffCanvasRectangles : setRectangles;
    const isInteractive = !isPreview;
    const isSelected =
      isInteractive &&
      rectangle.id === selectedObject?.id &&
      selectedObject?.type === "Rect";

    // Check if element is in selectedObjects array (for multi-selection)
    const isInSelectedObjects =
      selectedObjects &&
      selectedObjects.some(
        (obj) => obj.id === rectangle.id && obj.type === "Rect",
      );

    // When duplicate-to-selected-location is on, no drag (single or group). Otherwise draggable if selected or in multi-selection.
    const isDraggableElement =
      !isCloneMode &&
      !lockedAction &&
      (isSelected || (isInSelectedObjects && selectedObjects.length > 1));

    // Use provided opacity or element's own opacity, defaulting to 1
    const elementOpacity =
      opacity !== undefined
        ? opacity
        : rectangle.opacity !== undefined
          ? rectangle.opacity
          : 1;

    // Get current canvas dimensions (off-canvas stage is full triple-column width, not main width)
    const stageWidth = isOffCanvas
      ? sourceStageRef?.width() || getOffCanvasStageWidthPx()
      : sourceStageRef?.width() || window.innerWidth * 0.45;
    const stageHeight =
      sourceStageRef?.height() ||
      (parseInt(canvasHeight) / 100) * window.innerHeight;

    // Viewport culling: skip rendering rectangles far outside visible area
    const shouldAlwaysRender = isPreview || isSelected || isInSelectedObjects;
    if (!shouldAlwaysRender) {
      const stage = isOffCanvas
        ? offCanvasStageRef?.current
        : stageRef?.current;
      if (stage && !isElementInViewport(rectangle, "Rect", stage)) {
        return null;
      }
    }

    return (
      <RectangleElement
        key={isPreview ? `${rectangle.id}-preview` : rectangle.id}
        rectangle={{ ...rectangle, opacity: elementOpacity }}
        strokeColor={rectangle.stroke || strokeColor}
        isDraggable={isInteractive && !disableInteraction && isDraggableElement}
        disableInteraction={disableInteraction || !isInteractive}
        isSelected={isSelected}
        isElementBeingEdited={isElementBeingEdited}
        isOffCanvas={isOffCanvas}
        isPreview={isPreview}
        selectedObject={selectedObject}
        selectedObjects={selectedObjects}
        ignoreClickSelection={
          isInteractive && (!!lockedAction || !!isCloneMode)
        }
        crossStageGestureBegin={crossStageGestureBegin}
        crossStageGestureEnd={crossStageGestureEnd}
        images={images}
        stageRef={sourceStageRef}
        effectiveZoomLevel={effectiveZoomLevel}
        onCopyToOppositeCanvas={
          isInteractive && handleRectangleCopy
            ? () =>
                handleRectangleCopy(
                  rectangle.id,
                  isOffCanvas ? "offCanvas" : "main",
                )
            : undefined
        }
        onImageClick={(image) => {
          if (!isInteractive) {
            return;
          }
          if (lockedAction || isCloneMode) {
            return;
          }
          setSelectedImage(image);
          setSelectedObject({ ...image, type: "Image" });
          setSelectedObjects([{ ...image, type: "Image" }]);
          setShowTextBox(false);
          setSelectedText(null);
        }}
        onClick={
          isInteractive
            ? (e) => {
                if (!isPrimaryMouseButton(e.evt)) {
                  return;
                }
                // Disable selection if a tool is locked or clone mode is active
                if (lockedAction || isCloneMode) {
                  return;
                }

                // When multi-select is on and selection is from the other stage, prevent clicking on this stage's elements
                if (isOffCanvas && blockClicksOnOffCanvasElements) {
                  e?.evt?.preventDefault?.();
                  e?.evt?.stopPropagation?.();
                  return;
                }
                if (!isOffCanvas && blockClicksOnMainStageElements) {
                  e?.evt?.preventDefault?.();
                  e?.evt?.stopPropagation?.();
                  return;
                }

                // 🚫 CRITICAL: Check for cross-canvas selection BEFORE setting selectedObject
                // Build list of all currently selected elements
                const allSelectedElements = [];
                if (selectedObjects && selectedObjects.length > 0) {
                  selectedObjects.forEach((obj) => {
                    if (obj && obj.id && obj.type) {
                      allSelectedElements.push(obj);
                    }
                  });
                }
                if (
                  selectedObject &&
                  selectedObject.id &&
                  selectedObject.type
                ) {
                  const alreadyIncluded = allSelectedElements.some(
                    (obj) => obj.id === selectedObject.id,
                  );
                  if (!alreadyIncluded) {
                    allSelectedElements.push(selectedObject);
                  }
                }

                // Helper to check if an element is in main canvas
                const isElementInMainCanvas = (elementId, elementType) => {
                  if (!elementId || !elementType) return false;
                  switch (elementType) {
                    case "Rect":
                      return rectangles.some((r) => r.id === elementId);
                    case "Circle":
                      return circles.some((c) => c.id === elementId);
                    case "Image":
                      return images.some((i) => i.id === elementId);
                    case "Text":
                      return texts.some((t) => t.id === elementId);
                    case "Arrow":
                      // Check arrows array first
                      if (arrows.some((a) => a.id === elementId)) return true;
                      // Also check lines array for unified arrow elements (with arrow heads)
                      return lines.some((l) => {
                        if (l.id !== elementId) return false;
                        const hasStartArrow =
                          l.hasStartArrow === true ||
                          l.pointerAtBeginning === true;
                        const hasEndArrow =
                          l.hasEndArrow === true || l.pointerAtEnd === true;
                        return hasStartArrow || hasEndArrow;
                      });
                    case "Line":
                      // Lines without arrow heads are in the lines array
                      return lines.some((l) => {
                        if (l.id !== elementId) return false;
                        // Only return lines without arrow heads
                        return (
                          !l.hasStartArrow &&
                          !l.hasEndArrow &&
                          !l.pointerAtBeginning &&
                          !l.pointerAtEnd
                        );
                      });
                    case "DoubleSidedArrow":
                      // Check doubleSidedArrows array first
                      if (doubleSidedArrows.some((d) => d.id === elementId))
                        return true;
                      // Also check lines array for unified double-sided arrow elements
                      return lines.some((l) => {
                        if (l.id !== elementId) return false;
                        const hasStartArrow =
                          l.hasStartArrow === true ||
                          l.pointerAtBeginning === true;
                        const hasEndArrow =
                          l.hasEndArrow === true || l.pointerAtEnd === true;
                        return hasStartArrow && hasEndArrow;
                      });
                    default:
                      return false;
                  }
                };

                // If we have selected elements, check for cross-canvas conflict
                if (allSelectedElements.length > 0) {
                  const selectedElementsCanvasTypes = allSelectedElements.map(
                    (obj) => isElementInMainCanvas(obj.id, obj.type),
                  );
                  const allFromMainCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === true,
                  );
                  const allFromOffCanvas = selectedElementsCanvasTypes.every(
                    (isMain) => isMain === false,
                  );
                  const clickedElementIsMainCanvas = !isOffCanvas; // Rectangle is in main canvas if not isOffCanvas

                  // In multi-select mode, prevent cross-canvas selection entirely
                  // In normal mode, clear previous selections and continue
                  if (
                    (allFromMainCanvas && !clickedElementIsMainCanvas) ||
                    (allFromOffCanvas && clickedElementIsMainCanvas)
                  ) {
                    if (isMultiSelectMode) {
                      // In multi-select mode, don't allow selecting elements from different stages
                      // Keep existing selections and don't select the new element
                      e?.evt?.stopPropagation?.();
                      e?.evt?.preventDefault?.();
                      return;
                    } else {
                      // Normal mode cross-canvas behavior:
                      // first click only clears current selection, second click selects.

                      // Clear all current selections
                      setSelectedObject(null);
                      setSelectedObjects([]);
                      setShowTextBox(false);
                      setSelectedText(null);
                      if (setSelectedArrow) {
                        setSelectedArrow(null);
                      }
                      if (setSelectedDoubleSidedArrow) {
                        setSelectedDoubleSidedArrow(null);
                      }
                      if (setSelectedLine) {
                        setSelectedLine(null);
                      }
                      // Stop this click from selecting the newly clicked cross-canvas element.
                      e?.evt?.stopPropagation?.();
                      e?.evt?.preventDefault?.();
                      e.cancelBubble = true;
                      return;
                    }
                  }
                }

                const isSameElement =
                  selectedObject && selectedObject.id === rectangle.id;

                const rectAsObj = {
                  ...rectangle,
                  type: "Rect",
                  maintainAspectRatio: rectangle.maintainAspectRatio || false,
                };
                if (isMultiSelectMode) {
                  const currentSelectedObjects = selectedObjects || [];
                  const isAlreadySelected = currentSelectedObjects.some(
                    (obj) => obj.id === rectangle.id && obj.type === "Rect",
                  );
                  if (isAlreadySelected) {
                    cancelActiveGroupDrag?.();
                    const updated = currentSelectedObjects.filter(
                      (obj) => !(obj.id === rectangle.id && obj.type === "Rect"),
                    );
                    const nextPrimary = updated[0] || null;
                    try {
                      flushSync(() => {
                        setSelectedObjects(updated);
                        setSelectedObject(nextPrimary);
                      });
                    } catch (err) {
                      setSelectedObjects(updated);
                      setSelectedObject(nextPrimary);
                    }
                    let n = e?.target;
                    while (n) {
                      if (typeof n.stopDrag === "function") {
                        try {
                          n.stopDrag();
                        } catch (_) {
                          /* ignore */
                        }
                        break;
                      }
                      n = n.getParent?.() ?? null;
                    }
                    if (e?.evt) {
                      e.evt.stopPropagation();
                      e.evt.preventDefault();
                    }
                    e.cancelBubble = true;
                    return;
                  }

                  const merged = currentSelectedObjects.some(
                    (obj) =>
                      obj.id === selectedObject?.id &&
                      obj.type === selectedObject?.type,
                  )
                    ? [...currentSelectedObjects]
                    : selectedObject
                      ? [...currentSelectedObjects, selectedObject]
                      : [...currentSelectedObjects];
                  if (
                    !merged.some(
                      (obj) => obj.id === rectangle.id && obj.type === "Rect",
                    )
                  ) {
                    merged.push(rectAsObj);
                  }
                  // CRITICAL: flushSync so selection is committed before stage onClick/effects run; prevents something from overwriting with stale state
                  try {
                    flushSync(() => {
                      setSelectedObjects(merged);
                      setSelectedObject(rectAsObj);
                      if (setSelectedArrow) setSelectedArrow(null);
                      if (setSelectedDoubleSidedArrow)
                        setSelectedDoubleSidedArrow(null);
                      if (setSelectedLine) setSelectedLine(null);
                    });
                  } catch (e) {
                    setSelectedObjects(merged);
                    setSelectedObject(rectAsObj);
                    if (setSelectedArrow) setSelectedArrow(null);
                    if (setSelectedDoubleSidedArrow)
                      setSelectedDoubleSidedArrow(null);
                    if (setSelectedLine) setSelectedLine(null);
                  }
                  // Stop propagation so stage onClick never runs and can't overwrite selection
                  if (e?.evt) {
                    e.evt.stopPropagation();
                    e.evt.preventDefault();
                  }
                  e.cancelBubble = true;
                  // Do NOT call onClick(e) — the global click handler runs with stale state and can overwrite selectedObjects
                  return;
                }
                if (isSameElement) {
                  return;
                }
                const selectedObjectIsMainCanvas =
                  selectedObject?.id && selectedObject?.type
                    ? isElementInMainCanvas(selectedObject.id, selectedObject.type)
                    : null;
                const isSwitchingBetweenOffCanvasElements =
                  !isMultiSelectMode &&
                  !!selectedObject?.id &&
                  selectedObject.id !== rectangle.id &&
                  selectedObjectIsMainCanvas === false &&
                  isOffCanvas === true;
                if (isSwitchingBetweenOffCanvasElements) {
                  setSelectedObject(null);
                  setSelectedObjects([]);
                  setShowTextBox(false);
                  setSelectedText(null);
                  if (setSelectedArrow) setSelectedArrow(null);
                  if (setSelectedDoubleSidedArrow) setSelectedDoubleSidedArrow(null);
                  if (setSelectedLine) setSelectedLine(null);
                  e?.evt?.stopPropagation?.();
                  e?.evt?.preventDefault?.();
                  e.cancelBubble = true;
                  return;
                }

                setSelectedImage(null);
                setShowTextBox(false);
                setSelectedObject(rectAsObj);

                onClick(e);
              }
            : undefined
        }
        onDragMove={
          isInteractive
            ? (newAttrs, event) => {
                // If multiple elements are selected AND this element is part of the selection,
                // use group drag handler instead of individual drag
                const isInSelectedObjects = selectedObjects?.some(
                  (obj) => obj.id === newAttrs.id && obj.type === "Rect",
                );
                if (
                  selectedObjects &&
                  selectedObjects.length > 1 &&
                  isInSelectedObjects
                ) {
                  // Capture initial positions on the first call (pre-drag, from
                  // RectangleElement.onDragStart). handleGroupDragStart now sets
                  // isGroupDraggingRef.current = true immediately, so any subsequent call
                  // from the first real onDragMove is a safe no-op.
                  handleGroupDragStart?.(newAttrs.id, "Rect");
                  // Use group drag handler - pass event for accurate boundary detection
                  handleGroupDragMove?.(
                    newAttrs.id,
                    "Rect",
                    newAttrs.x,
                    newAttrs.y,
                    event,
                  );

                  // CRITICAL: Don't call setSelectedObject during group drag to prevent clearing multi-selection
                  // CRITICAL: Don't call handleDragPreview during group drag
                  // This prevents preview logic from interfering with group drag
                  // Elements should follow cursor freely during drag, only move on drag end
                  return;
                }

                // CRITICAL FIX: Do NOT transfer state arrays during drag move.
                // Moving the element between arrays/stages mid-drag can interrupt Konva dragging and fire dragEnd immediately.
                // Instead: determine target canvas based on mouse position and convert coordinates accordingly.
                let displayAttrs = { ...newAttrs };

                // Determine target canvas based on mouse position (like group drag handler)
                let targetCanvas = null;

                if (
                  event?.evt &&
                  sourceStageRef &&
                  offCanvasStageRef?.current
                ) {
                  try {
                    const clientX = event.evt.clientX;
                    const clientY = event.evt.clientY;

                    // CRITICAL FIX: Use stageRef?.current for main bounding box (like imageRenderer)
                    // not sourceStageRef which might be different
                    const mainStage = stageRef?.current;
                    const offCanvasStage = offCanvasStageRef.current;
                    const mainBBox = mainStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();
                    const offBBox = offCanvasStage
                      ?.container?.()
                      ?.getBoundingClientRect?.();

                    if (mainBBox && offBBox) {
                      const withinMain =
                        clientX >= mainBBox.left &&
                        clientX <= mainBBox.right &&
                        clientY >= mainBBox.top &&
                        clientY <= mainBBox.bottom;

                      const withinOff =
                        clientX >= offBBox.left &&
                        clientX <= offBBox.right &&
                        clientY >= offBBox.top &&
                        clientY <= offBBox.bottom;

                      targetCanvas = withinMain
                        ? "main"
                        : withinOff
                          ? "offCanvas"
                          : null;

                      // CRITICAL: If dragging over off-canvas area but element is on main canvas stage,
                      // convert the position to be relative to off-canvas stage origin
                      if (
                        targetCanvas === "offCanvas" &&
                        !isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        // Element is on main canvas stage but being dragged over off-canvas area
                        // Convert position from main canvas stage coordinates to off-canvas stage coordinates

                        // Step 1: Convert from main canvas stage coordinates to absolute/client coordinates
                        const absX = mainBBox.left + newAttrs.x;
                        const absY = mainBBox.top + newAttrs.y;

                        // Step 2: Convert from absolute/client coordinates to off-canvas stage coordinates
                        const convertedX = absX - offBBox.left;
                        const convertedY = absY - offBBox.top;

                        displayAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: true,
                        };
                      }
                      // CRITICAL: If dragging over main canvas area but element is on off-canvas stage,
                      // convert the position to be relative to main canvas stage origin
                      else if (
                        targetCanvas === "main" &&
                        isOffCanvas &&
                        mainBBox &&
                        offBBox
                      ) {
                        // Element is on off-canvas stage but being dragged over main canvas area
                        // Convert position from off-canvas stage coordinates to main canvas stage coordinates

                        // Step 1: Convert from off-canvas stage coordinates to absolute/client coordinates
                        const absX = offBBox.left + newAttrs.x;
                        const absY = offBBox.top + newAttrs.y;

                        // Step 2: Convert from absolute/client coordinates to main canvas stage coordinates
                        const convertedX = absX - mainBBox.left;
                        const convertedY = absY - mainBBox.top;

                        displayAttrs = {
                          ...newAttrs,
                          x: convertedX,
                          y: convertedY,
                          _isInOffCanvasDuringDrag: false,
                        };
                      }
                      // If element is already on the target canvas, clear/set the flag appropriately
                      else if (targetCanvas === "main" && !isOffCanvas) {
                        // Element is on main canvas and dragging over main canvas - clear flag
                        displayAttrs = {
                          ...newAttrs,
                          _isInOffCanvasDuringDrag: false,
                        };
                      } else if (targetCanvas === "offCanvas" && isOffCanvas) {
                        // Element is on off-canvas and dragging over off-canvas - set flag
                        displayAttrs = {
                          ...newAttrs,
                          _isInOffCanvasDuringDrag: true,
                        };
                      }
                    }
                  } catch (error) {
                  }
                }

                // Normal individual drag behavior - only if NOT part of a group
                handleDragPreview?.({
                  event,
                  newAttrs,
                  sourceStageRef,
                  element: rectangle,
                  type: "Rect",
                });
                // CRITICAL: Do NOT pass _isInOffCanvasDuringDrag into setSelectedObject/setSelectedObjects
                // during drag move. That flag causes findElementCanvas() to return isInOffCanvas: true,
                // which can move the transformer/editor to the off-canvas stage and interrupt Konva's drag
                // (triggering premature dragEnd). Strip the flag so the element stays considered on its
                // current stage until drag end. Same approach as imageRenderer (editorAttrsForSelection).
                const sourceStage = isOffCanvas ? "offCanvas" : "main";
                const previewTargetStage = targetCanvas ?? sourceStage;
                const selectionBase =
                  previewTargetStage !== sourceStage ? newAttrs : displayAttrs;
                const {
                  _isInOffCanvasDuringDrag: _unusedFlag,
                  ...attrsForSelection
                } = selectionBase;
                const selectionAttrs = {
                  ...attrsForSelection,
                  type: "Rect",
                  maintainAspectRatio: rectangle.maintainAspectRatio || false,
                  _isDragging: true,
                  _previewTargetStage: sourceStage,
                };
                const now = Date.now();
                if (now - _lastRectDragStateTime >= DRAG_STATE_THROTTLE_MS) {
                  _lastRectDragStateTime = now;
                  setSelectedObject(selectionAttrs);
                  setSelectedObjects((prev) => {
                    const existingIndex = prev.findIndex(
                      (obj) => obj.id === newAttrs.id && obj.type === "Rect",
                    );
                    if (existingIndex >= 0) {
                      const updated = [...prev];
                      updated[existingIndex] = selectionAttrs;
                      return updated;
                    }
                    return prev;
                  });
                }
              }
            : undefined
        }
        onTransformEnd={(newAttrs) => {
          if (!isInteractive) {
            return;
          }
          clearDebouncedTransformForKey?.(`rect-${newAttrs.id}`);
          let attrs =
            isOffCanvas && offCanvasStageRef?.current
              ? constrainOffCanvasLiveTransformAttrs(
                  rectangle,
                  newAttrs,
                  "Rect",
                  offCanvasStageRef.current,
                )
              : newAttrs;

          // Safety clamp for MAIN stage transforms:
          // In some timing/rounding cases, the RectangleElement internal clamp can still
          // leave x/y slightly outside after Konva transformer finalization.
          // This prevents persisted state from moving beyond the visible stage bounds.
          if (!isOffCanvas) {
            const stageObj = sourceStageRef?.current || sourceStageRef;
            if (stageObj?.width && stageObj?.height && attrs) {
              // RectangleElement already applies rotation-aware client-rect clamping.
              // This renderer-level fallback clamp is axis-aligned and can incorrectly
              // shift X/Y for rotated rectangles (e.g. ~90deg). Keep it only for
              // axis-aligned cases where width/height math is valid.
              let normalizedRotation = Number(attrs.rotation) || 0;
              normalizedRotation %= 360;
              if (normalizedRotation < 0) normalizedRotation += 360;
              const isAxisAligned =
                Math.abs(normalizedRotation - 0) < 0.01 ||
                Math.abs(normalizedRotation - 180) < 0.01;
              if (!isAxisAligned) {
                // Skip axis-aligned fallback clamp for rotated rectangles.
                // Their bounds were already corrected in RectangleElement.onTransformEnd.
                setRectState((prev) =>
                  prev.map((r) => (r.id === attrs.id ? { ...r, ...attrs } : r)),
                );
                setSelectedObject({
                  ...attrs,
                  type: "Rect",
                  maintainAspectRatio: rectangle.maintainAspectRatio || false,
                });
                setSelectedObjects([
                  {
                    ...attrs,
                    type: "Rect",
                    maintainAspectRatio: rectangle.maintainAspectRatio || false,
                  },
                ]);

                if (isSelected || isElementBeingEdited) {
                  setPendingChanges(true);
                  if (pendingChangesRef) {
                    pendingChangesRef.current = true;
                  }
                }
                return;
              }

              const stageWidth =
                typeof stageObj.width === "function"
                  ? stageObj.width()
                  : stageObj.width;
              const stageHeight =
                typeof stageObj.height === "function"
                  ? stageObj.height()
                  : stageObj.height;

              const rectWidth = Number(attrs.width) || 0;
              const rectHeight = Number(attrs.height) || 0;
              const BUFFER = 10;

              const effectiveInset = (requestedInset, s, rendered) => {
                const safeInset = Math.max(0, Number(requestedInset) || 0);
                const safeStage = Math.max(0, Number(s) || 0);
                const safeRendered = Math.max(0, Number(rendered) || 0);
                return Math.max(0, Math.min(safeInset, (safeStage - safeRendered) / 2));
              };

              const insetX = effectiveInset(BUFFER, stageWidth, rectWidth);
              const insetY = effectiveInset(BUFFER, stageHeight, rectHeight);

              const xMin = insetX;
              const xMax = stageWidth - rectWidth - insetX;
              const yMin = insetY;
              const yMax = stageHeight - rectHeight - insetY;

              const candidateX = Number(attrs.x) || 0;
              const candidateY = Number(attrs.y) || 0;

              const clampedX = Math.max(xMin, Math.min(xMax, candidateX));
              const clampedY = Math.max(yMin, Math.min(yMax, candidateY));

              attrs = {
                ...attrs,
                x: clampedX,
                y: clampedY,
              };
            }
          }
          if (justFinishedTransformRef) {
            justFinishedTransformRef.current = true;
          }
          // Calculate the updated state BEFORE calling setState
          // This ensures saveToHistory receives the correct updated state
          const updatedRectangles = isOffCanvas
            ? offCanvasRectangles.map((r) =>
                r.id === attrs.id ? { ...r, ...attrs } : r,
              )
            : rectangles.map((r) =>
                r.id === attrs.id ? { ...r, ...attrs } : r,
              );

          setRectState((prev) =>
            prev.map((r) => (r.id === attrs.id ? { ...r, ...attrs } : r)),
          );
          setSelectedObject({
            ...attrs,
            type: "Rect",
            maintainAspectRatio: rectangle.maintainAspectRatio || false,
          });
          setSelectedObjects([
            {
              ...attrs,
              type: "Rect",
              maintainAspectRatio: rectangle.maintainAspectRatio || false,
            },
          ]);

          // CRITICAL: Mark that there are pending changes so history will be saved on unselection
          // Always set pending changes when transform happens on a selected element
          // This ensures rotation and other transform properties are saved to history
          if (isSelected || isElementBeingEdited) {
            setPendingChanges(true);
            // CRITICAL: Also update the ref to ensure endElementEditing sees the change
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }

          // CRITICAL: Don't save to history here - history will be saved when element is unselected
          // This groups all transform changes (width, height, rotation, etc.) into a single history entry
        }}
        onDragEnd={
          isInteractive
            ? (id, newX, newY, mouseClientX, mouseClientY) => {
                clearDragPreview?.();

                // CRITICAL: Get the current rectangle properties from the Konva node, not from stale state
                // The node has the actual current rotation, scaleX, scaleY, etc. that the user sees
                // NOTE: sourceStageRef can be either a ref object (with .current) or the Stage object itself
                let currentRectangle = null;
                const stage = sourceStageRef?.current || sourceStageRef; // Handle both ref and direct stage
                if (stage) {
                  const node = stage.findOne(`#${id}`);
                  if (node) {
                    // Get the base rectangle from state to preserve all other properties
                    const baseRectangle = (
                      isOffCanvas ? offCanvasRectangles : rectangles
                    ).find((rect) => rect.id === id);

                    if (baseRectangle) {
                      // Get current rotation from the node
                      // The node always has the current visual rotation
                      let nodeRotation = node.rotation();
                      nodeRotation = nodeRotation % 360;
                      if (nodeRotation < 0) nodeRotation += 360;

                      // Create currentRectangle with properties from node (rotation, scaleX, scaleY, position)
                      // CRITICAL: Set rotation, scaleX, scaleY AFTER spreading baseRectangle to ensure they override any stale values
                      currentRectangle = {
                        ...baseRectangle,
                        x: newX,
                        y: newY,
                        // CRITICAL: These must come AFTER the spread to override any stale values from baseRectangle
                        rotation: nodeRotation, // Use the rotation from node
                        scaleX: node.scaleX(),
                        scaleY: node.scaleY(),
                      };
                    }
                  }
                }

                // Fallback: if we couldn't get the node, use the rectangle from state
                // But add a warning because this means we might lose rotation if state is stale
                if (!currentRectangle) {
                  currentRectangle = (
                    isOffCanvas ? offCanvasRectangles : rectangles
                  ).find((rect) => rect.id === id);
                  if (currentRectangle) {
                    // At least preserve the position update
                    currentRectangle = {
                      ...currentRectangle,
                      x: newX,
                      y: newY,
                    };
                  }
                }

                // Pass the current rectangle to handleElementDragEnd so it can preserve all properties
                // The currentRectangle has the latest rotation, scaleX, scaleY, etc. from the Konva node
                // CRITICAL: Pass mouseClientX and mouseClientY for group drag detection
                handleElementDragEnd({
                  id,
                  newX,
                  newY,
                  type: "Rect",
                  sourceStageRef,
                  mouseClientX, // Pass mouse coordinates for group drag detection
                  mouseClientY, // Pass mouse coordinates for group drag detection
                  mainState: rectangles,
                  offCanvasState: offCanvasRectangles,
                  setMainState: setRectangles,
                  setOffCanvasState: setOffCanvasRectangles,
                  setSelectedObjects,
                  currentRectangle, // Pass current rectangle with properties from Konva node
                });
              }
            : () => {}
        }
        onTransformStart={
          isInteractive
            ? () => {
                isTransformingRef.current.isTransforming = true;
                // Set ref so stage click that may fire on release (before onTransformEnd) does not clear selection
                if (justFinishedTransformRef) {
                  justFinishedTransformRef.current = true;
                }
                if (lastTransformStartTimeRef) {
                  lastTransformStartTimeRef.current = Date.now();
                }
                // Clear selection box refs so global mouseup doesn't process a stale box and clear selection
                if (selectionBoxRef) selectionBoxRef.current = null;
                if (selectionBoxStageRef) selectionBoxStageRef.current = null;
              }
            : undefined
        }
        onTransform={
          isInteractive
            ? (newAttrs) => {
                debouncedStateUpdate(() => {
                  setRectState((prev) =>
                    prev.map((r) =>
                      r.id === newAttrs.id ? { ...r, ...newAttrs } : r,
                    ),
                  );
                }, `rect-${newAttrs.id}`);

                // CRITICAL: Also update selectedObject and selectedObjects during transform for real-time editor updates
                setSelectedObject({
                  ...newAttrs,
                  type: "Rect",
                  maintainAspectRatio: rectangle.maintainAspectRatio || false,
                });
                setSelectedObjects((prev) => {
                  const existingIndex = prev.findIndex(
                    (obj) => obj.id === newAttrs.id && obj.type === "Rect",
                  );
                  if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = {
                      ...newAttrs,
                      type: "Rect",
                      maintainAspectRatio:
                        rectangle.maintainAspectRatio || false,
                    };
                    return updated;
                  }
                  return prev;
                });
              }
            : undefined
        }
        canvasWidth={stageWidth}
        canvasHeight={stageHeight}
        canvasResizeTrigger={canvasResizeTrigger}
      />
    );
  };
};
